import {
  Affix,
  Button,
  Center,
  Group,
  Loader,
  Notification,
  Popover,
  Portal,
  Progress,
  Stack,
  Text,
} from '@mantine/core';
import { useTimeout } from '@mantine/hooks';
import IconRefreshAlert from '@tabler/icons-react/dist/esm/icons/IconRefreshAlert.mjs';
import IconReload from '@tabler/icons-react/dist/esm/icons/IconReload.mjs';
import { createContext, useContext, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useRegisterSW } from 'virtual:pwa-register/react';
import {
  isAppUpdateProgressMessage,
  toInstallProgress,
  type InstallProgress,
} from '@/sw-install-progress';
import {
  claimAutomaticPwaReset,
  clearAutomaticPwaReset,
  resetPwaStorage,
} from './helpers/reset-pwa-storage';

const intervalMS = 60 * 1000;

const waitForInstallingWorker = (worker: ServiceWorker) =>
  new Promise<ServiceWorkerState>((resolve) => {
    if (worker.state === 'installed' || worker.state === 'redundant') {
      resolve(worker.state);
      return;
    }

    const onStateChange = () => {
      if (worker.state === 'installed' || worker.state === 'redundant') {
        worker.removeEventListener('statechange', onStateChange);
        resolve(worker.state);
      }
    };

    worker.addEventListener('statechange', onStateChange);
  });

const resolveRegistration = async (
  cached: ServiceWorkerRegistration | null,
) => {
  if (cached) {
    return cached;
  }

  if (!('serviceWorker' in navigator)) {
    return undefined;
  }

  try {
    const existing =
      (await navigator.serviceWorker.getRegistration?.()) ??
      (await navigator.serviceWorker.getRegistration?.('/'));

    if (existing) {
      return existing;
    }

    if (typeof navigator.serviceWorker.register === 'function') {
      return await navigator.serviceWorker.register('/sw.js');
    }
  } catch {
    return undefined;
  }

  return undefined;
};

const emptyProgress: InstallProgress = {
  loaded: 0,
  total: 0,
  percent: 0,
};

type AppUpdateApi = ReturnType<typeof useAppUpdateState>;

const AppUpdateContext = createContext<AppUpdateApi | null>(null);

const useAppUpdateState = () => {
  const [opened, setOpened] = useState(false);
  const [checking, setChecking] = useState(false);
  const [applying, setApplying] = useState(false);
  const [manualFound, setManualFound] = useState(false);
  const [autoFound, setAutoFound] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [status, setStatus] = useState<string | null>(null);
  const [progress, setProgress] = useState<InstallProgress>(emptyProgress);
  const registrationRef = useRef<ServiceWorkerRegistration | null>(null);
  const manualCheckRef = useRef(false);
  const dismissedRef = useRef(false);
  const { t } = useTranslation();
  dismissedRef.current = dismissed;
  const { start: startHidePopover, clear: clearHidePopover } = useTimeout(
    () => {
      setOpened(false);
      setStatus(null);
    },
    1200,
  );

  const recoverFromInstallFailure = () => {
    setProgress(emptyProgress);
    setAutoFound(false);
    setStatus(t('appUpdate.checkFailed', 'Failed to check for updates'));
    setOpened(true);
    manualCheckRef.current = false;

    if (!claimAutomaticPwaReset()) {
      return;
    }

    setResetting(true);
    void resetPwaStorage().catch((error) => {
      console.log('Automatic update cache reset failed:', error);
      setResetting(false);
      setStatus(t('appUpdate.resetFailed', 'Failed to reset the update cache'));
    });
  };

  useEffect(() => {
    if (
      !('serviceWorker' in navigator) ||
      !navigator.serviceWorker.addEventListener
    ) {
      return;
    }

    const onMessage = (event: MessageEvent) => {
      if (!isAppUpdateProgressMessage(event.data)) {
        return;
      }

      const next = toInstallProgress(event.data);
      setProgress(next);

      if (
        !manualCheckRef.current &&
        !dismissedRef.current &&
        next.total > 0 &&
        registrationRef.current?.active
      ) {
        setAutoFound(true);
      }
    };

    navigator.serviceWorker.addEventListener('message', onMessage);
    return () => {
      navigator.serviceWorker.removeEventListener('message', onMessage);
    };
  }, []);

  const {
    updateServiceWorker,
    offlineReady: [, setOfflineReady],
    needRefresh: [, setNeedRefresh],
  } = useRegisterSW({
    onNeedRefresh() {
      clearAutomaticPwaReset();
      if (manualCheckRef.current) {
        setManualFound(true);
        return;
      }

      setDismissed(false);
      setAutoFound(true);
      setStatus(null);
    },
    onRegisteredSW(swUrl, r) {
      console.log(`Service Worker registered at: ${swUrl}`);
      registrationRef.current = r ?? null;
      if (r) {
        console.log(
          `Service Worker registered. Checking for updates every ${intervalMS / 1000 / 60} minutes.`,
        );

        r.addEventListener?.('updatefound', () => {
          if (manualCheckRef.current || dismissedRef.current) {
            return;
          }

          const installing = r.installing;
          if (!installing) {
            return;
          }

          setAutoFound(true);
          setStatus(null);

          void waitForInstallingWorker(installing).then((state) => {
            if (state !== 'redundant') {
              return;
            }

            recoverFromInstallFailure();
          });
        });

        setInterval(async () => {
          if (!navigator.onLine) {
            console.log('Offline, skipping SW update check');
            return;
          }

          try {
            const response = await fetch('/sw.js', {
              cache: 'no-store',
              headers: { 'cache-control': 'no-cache' },
            });

            if (response?.ok) {
              console.log('Checking for SW updates...');
              await r.update();
            } else {
              console.log(
                `SW not available (${response?.status}), skipping update`,
              );
            }
          } catch (e) {
            console.log('Cannot check for SW updates:', e);
          }
        }, intervalMS);
      }
    },
    onRegisterError(error) {
      console.log(
        'Service Worker registration issue:',
        error?.message || error,
      );
    },
  });

  const showManualAvailable = () => {
    clearHidePopover();
    clearAutomaticPwaReset();
    setManualFound(true);
    setStatus(null);
    setOpened(true);
  };

  const handleUpdate = () => {
    setApplying(true);
    updateServiceWorker(true);
  };

  const handleClose = () => {
    if (applying || resetting) {
      return;
    }

    manualCheckRef.current = false;
    clearHidePopover();
    setOpened(false);
    setStatus(null);
    setManualFound(false);
    dismissedRef.current = true;
    setDismissed(true);
    setOfflineReady(false);
    setNeedRefresh(false);
  };

  const handleCheck = async () => {
    if (applying || resetting) {
      return;
    }

    if (!navigator.onLine) {
      setStatus(t('appUpdate.offline', 'You are offline'));
      setOpened(true);
      return;
    }

    clearHidePopover();
    manualCheckRef.current = true;
    setChecking(true);
    setManualFound(false);
    setStatus(null);
    setProgress(emptyProgress);
    setOpened(true);

    try {
      const r = await resolveRegistration(registrationRef.current);

      if (!r) {
        setStatus(
          t('appUpdate.notRegistered', 'App update service is not registered'),
        );
        return;
      }

      registrationRef.current = r;

      if (r.waiting) {
        showManualAvailable();
        return;
      }

      const response = await fetch('/sw.js', {
        cache: 'no-store',
        headers: { 'cache-control': 'no-cache' },
      });

      if (!response.ok) {
        setStatus(t('appUpdate.unavailable', 'Update service is unavailable'));
        return;
      }

      console.log('Checking for updates manually...');
      await r.update();

      if (r.installing) {
        const state = await waitForInstallingWorker(r.installing);
        if (state === 'redundant') {
          recoverFromInstallFailure();
          return;
        }
      }

      if (r.waiting) {
        showManualAvailable();
        return;
      }

      setManualFound(false);
      setProgress(emptyProgress);
      clearAutomaticPwaReset();
      setStatus(t('appUpdate.noUpdates'));
      manualCheckRef.current = false;
      clearHidePopover();
      startHidePopover();
    } catch (e) {
      console.log('Update check failed:', e);
      recoverFromInstallFailure();
    } finally {
      setChecking(false);
    }
  };

  const updateReady = manualFound || (autoFound && !dismissed);
  const downloading = progress.total > 0 && progress.percent < 100;
  const showProgress = applying || progress.total > 0;
  const canReload = updateReady && !applying && !downloading;
  const showAffix = autoFound && !dismissed && !opened && !applying;

  return {
    applying,
    autoFound,
    canReload,
    checking,
    dismissed,
    downloading,
    handleCheck,
    handleClose,
    handleUpdate,
    manualCheckRef,
    opened,
    progress,
    resetting,
    setManualFound,
    setOpened,
    showAffix,
    showProgress,
    status,
    t,
    updateReady,
  };
};

const useAppUpdate = () => {
  const value = useContext(AppUpdateContext);
  if (!value) {
    throw new Error('AppUpdate must be used within AppUpdateProvider');
  }
  return value;
};

export const AppUpdateProvider = ({ children }: { children?: ReactNode }) => {
  const value = useAppUpdateState();

  return (
    <AppUpdateContext.Provider value={value}>
      {children}
      <AppUpdateAffix />
      <AppUpdateOverlay />
    </AppUpdateContext.Provider>
  );
};

const AppUpdateOverlay = () => {
  const { applying, resetting, t } = useAppUpdate();

  if (!applying && !resetting) {
    return null;
  }

  return (
    <Portal>
      <Center
        data-testid='app-update-overlay'
        pos='fixed'
        inset={0}
        bg='var(--mantine-color-body)'
        style={{ zIndex: 400000 }}
      >
        <Stack align='center' gap='md'>
          <Loader size='lg' />
          <Text size='lg' fw={500}>
            {resetting
              ? t('appUpdate.resetting', 'Repairing update cache...')
              : t('appUpdate.reloading', 'Updating...')}
          </Text>
        </Stack>
      </Center>
    </Portal>
  );
};

const UpdateProgress = ({
  progress,
  showPreparing,
  t,
}: {
  progress: InstallProgress;
  showPreparing?: boolean;
  t: AppUpdateApi['t'];
}) => (
  <Stack gap={4}>
    <Progress
      data-testid='app-update-progress'
      value={progress.percent}
      animated={progress.percent < 100}
      size='sm'
    />
    <Group gap='xs' wrap='nowrap'>
      {(progress.total > 0 || showPreparing) && (
        <Text size='xs' c='dimmed'>
          {progress.total > 0
            ? t('appUpdate.files', {
                count: progress.total,
                defaultValue: '{{count}} files',
              })
            : t('appUpdate.preparing', 'Preparing update')}
        </Text>
      )}
      <Text size='xs' c='dimmed' ml='auto'>
        {`${progress.percent}%`}
      </Text>
    </Group>
  </Stack>
);

export const AppUpdateButton = () => {
  const {
    applying,
    autoFound,
    canReload,
    checking,
    dismissed,
    downloading,
    handleCheck,
    handleClose,
    handleUpdate,
    manualCheckRef,
    opened,
    progress,
    resetting,
    setManualFound,
    setOpened,
    showProgress,
    status,
    t,
    updateReady,
  } = useAppUpdate();

  return (
    <Popover
      withOverlay
      opened={opened}
      onChange={(next) => {
        if (applying || resetting) {
          return;
        }

        setOpened(next);
        if (!next) {
          manualCheckRef.current = false;
          setManualFound(false);
        }
      }}
      position='right-start'
      width={280}
      shadow='md'
      withArrow
      closeOnClickOutside={!applying && !resetting}
      closeOnEscape={!applying && !resetting}
    >
      <Popover.Target>
        <Button
          data-testid='check-updates'
          onClick={handleCheck}
          leftSection={
            autoFound && !dismissed ? (
              <IconReload size={16} />
            ) : (
              <IconRefreshAlert />
            )
          }
          w='auto'
          variant={autoFound && !dismissed ? 'light' : 'default'}
          color={autoFound && !dismissed ? 'yellow' : undefined}
          size='sm'
          loading={checking || applying || resetting}
        >
          {t('appUpdate.check')}
        </Button>
      </Popover.Target>
      <Popover.Dropdown data-testid='app-update-popover'>
        <Stack gap='xs'>
          <Text size='sm' data-testid='app-update-status'>
            {applying || (checking && downloading)
              ? t('appUpdate.downloading', 'Downloading update')
              : checking
                ? t('appUpdate.checking', 'Checking for updates...')
                : updateReady
                  ? t(
                      'appUpdate.updateAvailable',
                      'A new update is available. Reload to apply the update?',
                    )
                  : status}
          </Text>

          {showProgress && (
            <UpdateProgress progress={progress} showPreparing t={t} />
          )}

          {canReload && (
            <Group gap='xs'>
              <Button
                data-testid='app-update-reload'
                size='xs'
                variant='filled'
                onClick={handleUpdate}
              >
                {t('action.update', 'Reload Now')}
              </Button>
              <Button
                data-testid='app-update-later'
                size='xs'
                onClick={handleClose}
              >
                {t('text.later', 'Later')}
              </Button>
            </Group>
          )}
        </Stack>
      </Popover.Dropdown>
    </Popover>
  );
};

const AppUpdateAffix = () => {
  const {
    applying,
    canReload,
    downloading,
    handleClose,
    handleUpdate,
    progress,
    showAffix,
    showProgress,
    t,
  } = useAppUpdate();

  if (!showAffix) {
    return null;
  }

  return (
    <Affix
      position={{ bottom: 20, right: 20 }}
      zIndex={100000}
      data-testid='app-update-affix'
    >
      <Notification
        title={
          applying || downloading
            ? t('appUpdate.downloading', 'Downloading update')
            : t(
                'appUpdate.updateAvailable',
                'A new update is available. Reload to apply the update?',
              )
        }
        onClose={handleClose}
        withCloseButton={!applying}
        closeButtonProps={{
          'data-testid': 'app-update-affix-close',
        }}
      >
        <Stack gap='xs'>
          {showProgress && <UpdateProgress progress={progress} t={t} />}
          {canReload && (
            <Group>
              <Button
                data-testid='app-update-affix-reload'
                size='xs'
                variant='filled'
                onClick={handleUpdate}
              >
                {t('action.update', 'Reload Now')}
              </Button>
            </Group>
          )}
        </Stack>
      </Notification>
    </Affix>
  );
};

export const AppUpdate = () => (
  <AppUpdateProvider>
    <AppUpdateButton />
  </AppUpdateProvider>
);